import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import type {
  ProposalCollaborationEnvelope,
  ProposalNegotiationParty,
  ProposalReviewerApproval,
} from "work/proposals/negotiation/proposal-collaboration-contract";

type ReviewerIdentity = {
  userId: string;
  legalEntityId: string;
};

/** The server computes this from the endorsed submission and party content cursors. */
export function isReviewerApprovalCurrent(
  envelope: ProposalCollaborationEnvelope,
  reviewer: ReviewerIdentity,
  party: ProposalNegotiationParty = envelope.viewingParty,
): boolean {
  return envelope.reviewerApprovals.some(
    (approval) =>
      approval.party === party &&
      approval.reviewerUserId === reviewer.userId &&
      approval.reviewerLegalEntityId === reviewer.legalEntityId &&
      approval.isCurrent,
  );
}

/**
 * Proposal components still render their established reviewer date field. In
 * canonical mode, derive that field from versioned endorsements rather than
 * trusting snapshot metadata that can become stale after a new submission.
 */
export function applyCurrentReviewerApprovals(
  proposal: ProposalJSON,
  envelope: ProposalCollaborationEnvelope,
): ProposalJSON {
  return {
    ...proposal,
    clientReviewers: applyPartyApprovals(proposal.clientReviewers, envelope, "Client"),
    vendorReviewers: applyPartyApprovals(proposal.vendorReviewers, envelope, "Vendor"),
  };
}

function applyPartyApprovals(
  reviewers: ProposalJSON["clientReviewers"],
  envelope: ProposalCollaborationEnvelope,
  party: ProposalNegotiationParty,
): ProposalJSON["clientReviewers"] {
  return reviewers.map((reviewer) => {
    const approval = findCurrentApproval(envelope, reviewer, party);
    return {
      ...reviewer,
      dateApproved: approval?.approvedDate ?? null,
    };
  });
}

function findCurrentApproval(
  envelope: ProposalCollaborationEnvelope,
  reviewer: ReviewerIdentity,
  party: ProposalNegotiationParty,
): ProposalReviewerApproval | undefined {
  return envelope.reviewerApprovals.find(
    (approval) =>
      approval.party === party &&
      approval.reviewerUserId === reviewer.userId &&
      approval.reviewerLegalEntityId === reviewer.legalEntityId &&
      approval.isCurrent,
  );
}
